import React, { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";

import Button from "components/Button";

import UpdateExpirationModal from "./UpdateExpirationModal";
import { IbanInvoiceValidity } from "./UpdateExpirationModal/validation";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { getInvoiceExpirationSettings } from "store/features/invoiceExpirationSettingsSlice";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

const SettingsMain: React.FC = () => {
  const dispatch = useAppDispatch();
  const invoiceExpirationSettings = useAppSelector(
    (state) => state.invoiceExpirationSettings
  );

  const days = invoiceExpirationSettings?.days;
  const hours = invoiceExpirationSettings?.hours;

  const [contentLoading, setContentLoading] = useState(true);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  useEffect(() => {
    // Fetch iban expiration settings on tab click
    dispatch(getInvoiceExpirationSettings()).finally(() =>
      setContentLoading(false)
    );
  }, []);

  const onUpdateInvoiceExpiration = () => {
    analytics().sendEvent(AnalyticsEvents.settings_update_exp_modal_open);
    setShowUpdateModal(true);
  };

  const onCloseUpdateModal = () => {
    analytics().sendEvent(AnalyticsEvents.settings_update_exp_modal_close);
    setShowUpdateModal(false);
  };

  const renderDaysText = (days?: number) => {
    if (!days) {
      return "";
    }

    if (days > 10 && days < 20) {
      return ` ${days} днів`;
    }

    const rest = days % 10;

    switch (rest) {
      case 1:
        return ` ${days} день`;
      case 2:
      case 3:
      case 4:
        return ` ${days} дні`;
      default:
        return ` ${days} днів`;
    }
  };

  const renderHoursText = (hours?: number) => {
    if (!hours) {
      return "";
    }

    if (hours > 10 && hours < 20) {
      return ` ${hours} годин`;
    }

    const rest = hours % 10;

    switch (rest) {
      case 1:
        return ` ${hours} годину`;
      case 2:
      case 3:
      case 4:
        return ` ${hours} години`;
      default:
        return ` ${hours} годин`;
    }
  };

  return (
    <>
      <Box width="100%" sx={{ mb: { xs: 2, sm: 3 } }}>
        <Typography sx={{ mb: 0.5 }} variant="h3">
          Налаштування
        </Typography>
        <Typography color="grey700">
          Керуйте вашими глобальними налаштуваннями.
        </Typography>
      </Box>
      <Box width="100%">
        <Typography sx={{ mb: 0.5 }}>Термін дії посилання</Typography>
        {contentLoading ? (
          <Skeleton
            animation="wave"
            height={40}
            sx={{ width: { xs: "80%", sm: "40%" } }}
          />
        ) : (
          <Typography color="grey700" sx={{ mb: 2 }}>
            Всі посилання{" "}
            <Typography component="span" color="grey900" fontWeight="bold">
              діють
              {days || hours
                ? `${renderDaysText(days)}${renderHoursText(hours)}`
                : " завжди"}
            </Typography>
            .
          </Typography>
        )}

        <Button
          variant="contained"
          color="secondary"
          withBorder
          disabled={contentLoading}
          onClick={onUpdateInvoiceExpiration}
        >
          Змінити термін дії
        </Button>
      </Box>
      <UpdateExpirationModal
        isOpen={showUpdateModal}
        onClose={onCloseUpdateModal}
        defaultValues={{
          days,
          hours,
          validity:
            days || hours
              ? IbanInvoiceValidity.Limited
              : IbanInvoiceValidity.Always,
        }}
      />
    </>
  );
};

export default SettingsMain;
